import { constants } from '@lmc/common'
import { UserRole } from '@lmc/ts-common'
import { Button, GradientPageHeader, Icon, IconType } from '@lmc/ui-kit'
import Search from '@lmc/ui-kit/src/dataEntry/Input/components/Search/Search'
import Text from '@lmc/ui-kit/src/general/Typography/Text/Text'
import { useRoleAccess } from 'components/AccessControl'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import type React from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

type ResidentListHeaderProps = {
  openModal: () => void
  onSearch: Dispatch<SetStateAction<string>>
  title?: string
  searchPlaceholder?: string
  buttonTitle?: string
  isPro?: boolean
  residentCount?: number
  preferredPeopleNominative?: string
  testId?: string
}

const StyledContainer = styled.div`
  max-width: 100%;
  margin-top: 24px;
  margin-bottom: 0px;
  margin-left: 24px;
  margin-right: 24px;
`

const StyledChild = styled.div`
  display: flex;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
    margin-right: auto;
  }
`

const StyledSearch = styled(Search)`
  margin-right: 10px;
  width: 200px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin-bottom: 10px;
  }
`

const StyledMail = styled(Icon)`
  color: white;
`

const TooltipContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const StyledTooltipText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color11};
`

const ResidentListHeader: React.FC<ResidentListHeaderProps> = ({
  openModal,
  onSearch,
  title,
  searchPlaceholder,
  buttonTitle,
  isPro = false,
  residentCount = 0,
  preferredPeopleNominative,
  testId
}) => {
  const { t } = useTranslation(NAMESPACE.GENERAL)
  const hasRolePermission = useRoleAccess(
    [UserRole.careHomeAdmin],
    [
      {
        i: 'create',
        this: 'Resident'
      }
    ]
  )

  const requiresProUpgrade =
    !isPro && residentCount >= constants.MAX_FREE_RESIDENTS

  return (
    <StyledContainer
      data-testid={testId ? `${testId}-header` : 'residents-list-header'}
    >
      <GradientPageHeader title={title as string} gradient>
        <StyledChild
          data-testid={
            testId
              ? `${testId}-header-actions`
              : 'residents-list-header-actions'
          }
        >
          <StyledSearch
            placeholder={searchPlaceholder}
            onChange={(value) => onSearch(value?.target?.value)}
            data-testid={testId ? `${testId}-search` : 'residents-list-search'}
          />
          <Button
            onClick={openModal}
            variant="primary"
            icon={<StyledMail customIcon={IconType.Mail} />}
            disabled={requiresProUpgrade}
            disabledByPermission={requiresProUpgrade || !hasRolePermission}
            data-testid={
              testId ? `${testId}-add-button` : 'residents-list-add-button'
            }
            permissionDisabledText={
              requiresProUpgrade ? (
                <TooltipContent>
                  <StyledTooltipText level={1}>
                    {t(generalConstants.PRO_UPGRADE_TOOLTIP, {
                      count: constants.MAX_FREE_RESIDENTS,
                      peopleNominative:
                        preferredPeopleNominative?.toLowerCase() || 'residents'
                    })}
                    <Link to="/pro-advert">
                      {t(generalConstants.PRO_UPGRADE_LINK_TEXT)}
                    </Link>
                  </StyledTooltipText>
                </TooltipContent>
              ) : undefined
            }
            withDisabledTooltipIcon={!requiresProUpgrade}
          >
            {buttonTitle}
          </Button>
        </StyledChild>
      </GradientPageHeader>
    </StyledContainer>
  )
}

export default ResidentListHeader
